import { HillList } from '@/components/hill-list';
import { ImportHillDialog } from '@/components/import-hill-dialog';
import { Button } from '@/components/ui/button';
import { hillsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { sessionQueryOptions } from '@/lib/session-query';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';

/**
 * `/hills` — authenticated list page.
 *
 * The auth guard runs in `beforeLoad` (not in a component) so an unauthenticated
 * visitor is redirected before any component mounts — no flicker of the list
 * skeleton before the /login redirect lands.
 *
 * The hills query itself is fired from the component, not via `loader`. Rationale:
 * we want the page shell (header, "Import project" button) to show immediately
 * so the user has something to click on while the list loads. A loader would
 * block the whole route transition until the fetch resolves.
 */
export const Route = createFileRoute('/hills/')({
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData(sessionQueryOptions());
    if (!session) {
      throw redirect({ to: '/login' });
    }
  },
  component: HillsListPage,
});

/**
 * Exported for testing. The route only needs this as `component`, but
 * rendering it directly in tests avoids the RouterProvider setup dance
 * and lets us exercise the loading / error / refetch branches in isolation.
 */
export function HillsListPage() {
  const hillsQuery = useQuery({
    queryKey: queryKeys.hillsList(),
    queryFn: () => hillsApi.list(),
    staleTime: 30_000,
  });

  return (
    <section className="space-y-6">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-bold text-3xl text-brand-900 tracking-tight">Hills</h1>
          <p className="text-base text-slate-600">
            One hill per GitLab project. Drag milestones as they progress.
          </p>
        </div>
        <ImportHillDialog />
      </header>

      {hillsQuery.isLoading ? (
        // `aria-busy` + the implicit `role="status"` on `<output>` tell
        // assistive tech that the region is *deliberately* being filled —
        // without it, the three grey rectangles read as three meaningless
        // groups. A single polite status announcement (via the aria-label)
        // is enough; each skeleton tile itself is aria-hidden. We use
        // `<output>` rather than `<div role="status">` so Biome's
        // `a11y/useSemanticElements` rule stays satisfied (same pattern as
        // `ViewModeBanner` in the detail route).
        <output
          aria-busy="true"
          aria-label="Loading hills"
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              aria-hidden="true"
              className="h-40 animate-pulse rounded-lg border border-slate-200 bg-slate-50"
            />
          ))}
        </output>
      ) : hillsQuery.isError ? (
        <div
          role="alert"
          className="flex flex-col gap-4 rounded-lg border border-red-200 bg-red-50 p-6 text-red-700 sm:flex-row sm:items-start sm:justify-between"
        >
          <div>
            <p className="font-medium">Couldn't load hills.</p>
            <p className="mt-1 text-sm">{hillsQuery.error.message}</p>
          </div>
          {/*
            `refetch()` respects the query's current state — it will not
            trigger a second request if one is already in flight. We disable
            the button anyway to make the pending state visible and keep the
            user from hammering a failing endpoint.
          */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => hillsQuery.refetch()}
            disabled={hillsQuery.isFetching}
            className="shrink-0 border-red-300 text-red-700 hover:bg-red-100"
          >
            {hillsQuery.isFetching ? 'Retrying…' : 'Try again'}
          </Button>
        </div>
      ) : (
        <HillList hills={hillsQuery.data?.hills ?? []} />
      )}
    </section>
  );
}
